import { AprData, BalanceData } from '../fetch-balances/types'

/**
 * Calculates weighted average
 * @param items Array of { value: number, weight: number }
 * @returns Weighted average, or 0 if total weight is 0
 */
export function calculateWeightedAverage(
  items: { value: number; weight: number }[],
): number {
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0)
  if (totalWeight === 0) return 0
  const weightedSum = items.reduce(
    (sum, item) => sum + item.value * item.weight,
    0,
  )
  return weightedSum / totalWeight
}

/**
 * Calculates net APR for a sub-account based on deposits and debt
 */
export function calculateNetApr(
  aprData: AprData,
  balanceData: BalanceData,
): number {
  const deposits = balanceData.deposits
  const debt = balanceData.debt
  const nav = deposits - debt

  if (nav === 0) return 0

  // Deposit APR is positive, borrow APR is negative (cost)
  const depositIncome = deposits * (aprData.depositApr / 100)
  const borrowCost = debt * (aprData.borrowApr / 100)
  const rewardIncome =
    deposits * (aprData.rewardDepositApr / 100) +
    debt * (aprData.rewardBorrowApr / 100)

  const netIncome = depositIncome - borrowCost + rewardIncome
  return (netIncome / nav) * 100
}

/**
 * Calculates leverage ratio
 * @returns leverage = deposits / netWorth, or 0 if netWorth <= 0
 */
export function calculateLeverage(deposits: number, netWorth: number): number {
  return netWorth > 0 ? deposits / netWorth : 0
}

/**
 * Calculates overall net APR from totals
 */
export function calculateOverallNetApr(
  totalDepositsUSD: number,
  totalDebtUSD: number,
  avgDepositApr: number,
  avgBorrowApr: number,
): number {
  const totalNetWorth = totalDepositsUSD - totalDebtUSD
  if (totalNetWorth === 0) return 0

  return (
    ((totalDepositsUSD * avgDepositApr - totalDebtUSD * avgBorrowApr) /
      Math.abs(totalNetWorth)) *
    (totalNetWorth > 0 ? 1 : -1)
  )
}

export interface AprWeightItem {
  deposits: number
  debt: number
  nav: number
  apr: number
  depositApr: number
  borrowApr: number
  rewardApr: number
  rewardDepositApr: number
  rewardBorrowApr: number
  intrinsicApr: number
  intrinsicDepositApr: number
  intrinsicBorrowApr: number
}

/**
 * Computes all weighted APR fields from a set of items.
 *
 * Weighting rules:
 * - Deposit-side APRs (depositApr, rewardDepositApr, intrinsicDepositApr) → weighted by deposits
 * - Borrow-side APRs (borrowApr, rewardBorrowApr, intrinsicBorrowApr) → weighted by debt
 * - Net APRs (apr, rewardApr, intrinsicApr) → weighted by nav
 */
export function calculateWeightedAprs(items: AprWeightItem[]) {
  const wa = (field: keyof AprWeightItem, weightField: keyof AprWeightItem) =>
    calculateWeightedAverage(
      items
        .filter((i) => i[weightField] !== 0)
        .map((i) => ({ value: i[field] as number, weight: Math.abs(i[weightField] as number) })),
    )

  return {
    apr: wa('apr', 'nav'),
    depositApr: wa('depositApr', 'deposits'),
    borrowApr: wa('borrowApr', 'debt'),
    rewardApr: wa('rewardApr', 'nav'),
    rewardDepositApr: wa('rewardDepositApr', 'deposits'),
    rewardBorrowApr: wa('rewardBorrowApr', 'debt'),
    intrinsicApr: wa('intrinsicApr', 'nav'),
    intrinsicDepositApr: wa('intrinsicDepositApr', 'deposits'),
    intrinsicBorrowApr: wa('intrinsicBorrowApr', 'debt'),
  }
}
